/**
 * The last line of defence for a render error after mount.
 *
 * Without a boundary at the root, React answers an error thrown while
 * rendering, anywhere no feature boundary (./error-boundary.tsx's callers)
 * covers, by unmounting the whole root. On a prerendered page that deletes
 * content the visitor was already reading and leaves a blank page. This
 * catches it, shows a fallback that says what happened and offers a reload,
 * and reports the error.
 *
 * Reporting is explicit because a caught error goes nowhere on its own: React
 * hands it to the root's `onCaughtError` (a console.error by default), never
 * to `window`'s `error` event, so ../logging/earlyErrorCapture.ts would not
 * see it. It is routed into that boundary as `infra.error.unhandled` with
 * `source: "react.error-boundary"`, through LoggerProvider's Logger once that
 * has adopted it. It reports once per failure: the boundary latches on its
 * fallback, and the fallback renders nothing that can throw, so a render that
 * fails in a loop has no loop to fail in here.
 *
 * What it cannot catch is an error in the providers above it (see
 * ../app-root.tsx) or outside rendering (event handlers, promises); those
 * still reach earlyErrorCapture's window listeners.
 */
import type { ReactNode } from "react";
import { ErrorBoundary } from "@bindery/ui/components/error-boundary";
import { toError } from "@/logging";
import { reportUnhandledError } from "@/logging/earlyErrorCapture";

export function RootErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={() => <RenderFailed />}
      onError={(error) => {
        reportUnhandledError(
          toError(error, "Render error"),
          "react.error-boundary",
        );
      }}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * The fallback: no router, no nav, no Convex query — only what cannot fail.
 * It keeps the reader's own fallback voice (reader/reading-frame.tsx) so a
 * failure still looks like this app. `role="alert"` so a screen reader hears
 * that the page changed under it, which it otherwise would not.
 */
function RenderFailed() {
  return (
    <main
      role="alert"
      className="mx-auto flex min-h-svh max-w-2xl flex-col items-center justify-center gap-4 px-6 text-center"
    >
      <h1 className="text-foreground font-serif text-2xl">
        Something went wrong
      </h1>
      <p className="text-muted-foreground font-serif text-sm">
        This page hit an error it could not recover from. Reloading usually
        fixes it.
      </p>
      <button
        type="button"
        onClick={() => {
          window.location.reload();
        }}
        className="text-primary font-serif underline underline-offset-4"
      >
        Reload the page
      </button>
    </main>
  );
}
